// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Link from "@insite/mobius/Link";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ShareThis from "@mcmc/App/Components/ShareThis/ShareThis";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import exportEvents from "@mcmc/App/Handlers/Pages/ProductList/ExportEvents";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { totalSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import Tooltip from "@mcmc/MobiusOverrides/Tooltip";
import { exportIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const path =
        selectedCategoryPath || (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    return {
        userEmail: state.context.session.email,
        search: location.search,
        path,
    };
};

const mapDispatchToProps = {
    setBreadcrumbActions,
    exportEvents,
};

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCategoryContext &
    ReturnType<typeof mapStateToProps>;

const EventsListingActionsWidget: React.FC<Props> = ({
    setBreadcrumbActions,
    category,
    userEmail,
    exportEvents,
    path,
    search,
}) => {
    const [email, setEmail] = React.useState(userEmail);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [emailError, setEmailError] = React.useState("");
    const [isExporting, setIsExporting] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);

    const toasterContext = React.useContext(ToasterContext);

    const isSearchPath = () => {
        return !!path.match(/^\/search\b/i);
    };

    const resetForm = () => {
        setIsModalOpen(false);
        setIsSubmitted(false);
        setEmail("");
    };

    const handleRequestExport = () => {
        if (email) {
            handleExportData();
            return;
        }
        setIsModalOpen(true);
    };

    const validateEmailTo = () => {
        if (!validateEmail(email)) {
            setEmailError(email ? translate("Enter a valid email address") : translate("Required"));
            return false;
        }

        return true;
    };

    const handleExportData = () => {
        setIsSubmitted(true);

        if (!validateEmailTo()) {
            return;
        }

        setIsExporting(true);

        exportEvents({
            path,
            queryString: search,
            isSearch: isSearchPath(),
            shareToEmail: email,
            onSuccess: (result: any) => {
                toasterContext.addToast({ body: siteMessage("MCMC_EventExport_Success"), messageType: "success" });
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete: (result: any) => {
                setIsExporting(false);
            },
        } as any);

        if (!userEmail) {
            resetForm();
        }
    };

    React.useEffect(() => {
        validateEmailTo();
    }, [email]);

    React.useEffect(() => {
        if (category?.properties.isEventsCategory?.toLowerCase() === "true") {
            setBreadcrumbActions({
                breadcrumbActions: (
                    <StyledWrapper {...totalSectionGlobalStyles.breadcrumbActionsWrapper}>
                        <ShareThis />
                        <Tooltip
                            text={
                                isExporting
                                    ? translate("Exporting Events...")
                                    : siteMessage("McMc_Export_Events_To_PDF")
                            }
                            placement="bottom-end"
                            triggerComponent={
                                <Link
                                    {...exportIconStyle}
                                    onClick={handleRequestExport}
                                    data-test-selector="eventListingPage_export"
                                    id="exporttraining"
                                    disabled={isExporting}
                                >
                                    <VisuallyHidden>{translate("Export Exents to PDF")}</VisuallyHidden>
                                </Link>
                            }
                        ></Tooltip>
                    </StyledWrapper>
                ),
            });

            return () => {
                setBreadcrumbActions({
                    breadcrumbActions: null,
                });
            };
        }
    }, [category, isExporting]);

    return (
        <TwoButtonModal
            headlineText={translate("Export Events")}
            modalIsOpen={isModalOpen}
            messageText={siteMessage("McMc_ExportEventsModalMessage")}
            cancelButtonText={translate("Cancel")}
            submitButtonText={translate("Export")}
            onCancel={resetForm}
            onSubmit={handleExportData}
            submitTestSelector="exportEventsSubmit"
        >
            <TextField
                label={translate("Email To")}
                required
                maxLength={70}
                onChange={event => setEmail(event.target.value)}
                error={isSubmitted && emailError}
            />
        </TwoButtonModal>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(EventsListingActionsWidget)),
    definition: {
        group: "Product List",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
